'use client';
import * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../cn';

export function CodeBlock({ code, className, language, maxHeight = 420 }: { code: string | object; className?: string; language?: string; maxHeight?: number }) {
  const text = typeof code === 'string' ? code : JSON.stringify(code, null, 2);
  return (
    <pre className={cn('overflow-auto rounded-[var(--radius)] bg-surface-3 p-4 text-[12.5px] leading-[1.5] text-text font-[var(--font-mono)]', className)} style={{ maxHeight }} data-language={language}>
      <code>{text}</code>
    </pre>
  );
}

function Primitive({ v }: { v: unknown }) {
  if (v === null || v === undefined) return <span className="text-text-3">null</span>;
  if (typeof v === 'string') return <span className="text-[color-mix(in_srgb,var(--green)_70%,var(--text))] break-all">"{v}"</span>;
  if (typeof v === 'number') return <span className="text-accent tabular">{String(v)}</span>;
  if (typeof v === 'boolean') return <span className="text-purple">{String(v)}</span>;
  return <span>{String(v)}</span>;
}

function Node({ name, value, depth, defaultOpen }: { name?: string; value: unknown; depth: number; defaultOpen: number }) {
  const isObj = value !== null && typeof value === 'object';
  const [open, setOpen] = React.useState(depth < defaultOpen);
  if (!isObj) {
    return (
      <div className="flex gap-1.5 py-px" style={{ paddingLeft: depth * 14 }}>
        {name !== undefined ? <span className="text-text-2 shrink-0">{name}:</span> : null}
        <Primitive v={value} />
      </div>
    );
  }
  const entries = Array.isArray(value) ? value.map((v, i) => [String(i), v] as const) : Object.entries(value as Record<string, unknown>);
  const summary = Array.isArray(value) ? `[${entries.length}]` : `{${entries.length}}`;
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 py-px text-left hover:bg-fill-3 rounded-[4px] w-full" style={{ paddingLeft: Math.max(0, depth * 14 - 14) }}>
        <ChevronRight className={cn('size-3 text-text-3 transition-transform', open && 'rotate-90')} />
        {name !== undefined ? <span className="text-text-2">{name}:</span> : null}
        <span className="text-text-3">{summary}</span>
      </button>
      {open ? entries.map(([k, v]) => <Node key={k} name={k} value={v} depth={depth + 1} defaultOpen={defaultOpen} />) : null}
    </div>
  );
}

/** Collapsible JSON tree for previews and proposal actions. */
export function JsonView({ value, className, defaultOpen = 2 }: { value: unknown; className?: string; defaultOpen?: number }) {
  return (
    <div className={cn('rounded-[var(--radius)] bg-surface-3 p-3 text-[12.5px] leading-[1.5] font-[var(--font-mono)] overflow-auto', className)}>
      <Node value={value} depth={0} defaultOpen={defaultOpen} />
    </div>
  );
}
